import { useCallback, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';
import { consultationsApi, type ConsultationBooking } from '@/api/consultationsApi';
import { paymentsApi, type InitiatedPayment } from '@/api/paymentsApi';
import { openCheckout } from '@/lib/cashfree';
import { pollPaymentStatus } from '@/lib/paymentPolling';
import { clearIdempotencyKey, idempotencyKeyFor } from '@/lib/idempotency';
import { captureReturnTo, withReturnTo } from '@/lib/returnTo';

type Stage = 'idle' | 'holding' | 'paying' | 'confirming' | 'done';

function errorMessage(err: unknown, fallback: string): string {
  const data = (err as { response?: { data?: { error?: string } } })?.response?.data;
  return data?.error || fallback;
}

/**
 * Hold a slot, pay for it, confirm. Login is required before the hold, because
 * a hold belongs to an account; browsing and picking a time stay open to
 * everyone. The key is generated once per attempt so a retry after a network
 * blip reuses the same hold instead of taking a second slot.
 */
export function useConsultationCheckout(onConfirmed?: (b: ConsultationBooking) => void) {
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [stage, setStage] = useState<Stage>('idle');
  const [booking, setBooking] = useState<ConsultationBooking | null>(null);
  const inFlight = useRef(false);

  const start = useCallback(
    async (date: string, time: string, notes?: string, couponCode?: string) => {
      if (!isAuthenticated) {
        // Carry the chosen slot back so they land where they left off.
        navigate(withReturnTo('/login', captureReturnTo(location)), {
          state: { returnTo: `${location.pathname}?date=${date}&time=${time}` },
        });
        return;
      }
      if (inFlight.current) return;
      inFlight.current = true;

      const intent = `consultation:${date}:${time}`;
      let paymentId: string | null = null;
      try {
        setStage('holding');
        const holdRes = await consultationsApi.hold(date, time, {
          notes,
          idempotencyKey: idempotencyKeyFor(intent),
        });
        const held = holdRes.data.consultation;
        setBooking(held);

        setStage('paying');
        const payRes = await paymentsApi.initiate({
          amount: held.total_amount,
          booking_id: held.consultation_id,
          coupon_code: couponCode,
          description: 'Online physiotherapy consultation',
        });
        const payment = (payRes.data as { payment?: InitiatedPayment } | undefined)?.payment;
        if (!payment?.payment_id) throw new Error('Payment could not be started');
        paymentId = payment.payment_id;

        if (payment.provider !== 'cashfree' || !payment.payment_session_id) {
          throw new Error('Online payment is unavailable right now');
        }
        const outcome = await openCheckout(payment.payment_session_id, payment.gateway_env);
        if (outcome === 'unavailable') {
          void paymentsApi.abandon(paymentId).catch(() => {});
          toast.error('Could not open the payment window. Please try again.');
          setStage('idle');
          return;
        }

        setStage('confirming');
        const status = await pollPaymentStatus(paymentId);
        if (status === 'success') {
          const fresh = await consultationsApi.getBooking(held.consultation_id);
          setBooking(fresh.data.consultation);
          // Paid for: the next attempt on this slot is a new intent.
          clearIdempotencyKey(intent);
          setStage('done');
          onConfirmed?.(fresh.data.consultation);
          toast.success('Consultation confirmed');
          return;
        }
        // Not paid: free the consultant's slot now rather than waiting for the sweep.
        void paymentsApi.abandon(paymentId).catch(() => {});
        clearIdempotencyKey(intent);
        setStage('idle');
        toast[status === 'failed' ? 'error' : 'info'](
          status === 'failed'
            ? 'Payment did not go through. The slot has been released.'
            : 'We have not had confirmation yet. If money was debited your booking updates automatically.',
        );
      } catch (err) {
        if (paymentId) void paymentsApi.abandon(paymentId).catch(() => {});
        clearIdempotencyKey(intent);
        setStage('idle');
        toast.error(errorMessage(err, 'Something went wrong. Please try again.'));
      } finally {
        inFlight.current = false;
      }
    },
    [isAuthenticated, navigate, location, onConfirmed],
  );

  return { stage, booking, start, busy: stage !== 'idle' && stage !== 'done' };
}
